import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import { motion } from 'framer-motion';
import { HeroProps } from '../types/hero';
import * as THREE from 'three';

function ParticleField() {
  const ref = useRef<THREE.Points>(null);
  
  const positions = useMemo(() => {
    // Reduced from 2500 to 400 for better performance on 8GB Macs
    const positions = new Float32Array(400 * 3);
    for (let i = 0; i < 800; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    return positions;
  }, []);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime * 0.015;
      ref.current.rotation.y = state.clock.elapsedTime * 0.025;
    }
  });

  return (
    <Points ref={ref} positions={positions} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        color="#FACC15"
        size={0.012}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        opacity={0.5}
      />
    </Points>
  );
}

function FloatingOrb({ position, scale, speed, opacity }: { position: [number, number, number]; scale: number; speed: number; opacity: number }) {
  const ref = useRef<THREE.Mesh>(null);
  
  useFrame((state) => {
    if (ref.current) {
      ref.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * speed) * 0.25;
      ref.current.rotation.y = state.clock.elapsedTime * 0.15 * speed;
    }
  });

  return (
    <mesh ref={ref} position={position} scale={scale}>
      <icosahedronGeometry args={[1, 1]} />
      <meshStandardMaterial
        color="#FACC15"
        wireframe
        transparent
        opacity={opacity}
        emissive="#FACC15"
        emissiveIntensity={0.2}
      />
    </mesh>
  );
}

function Scene3D() {
  return (
    <>
      <ParticleField />
      <FloatingOrb position={[-2.5, 0.5, -2]} scale={0.6} speed={0.4} opacity={0.12} />
      <FloatingOrb position={[2, -1, -1.5]} scale={0.4} speed={0.6} opacity={0.08} />
      <FloatingOrb position={[0.5, 2, -2.5]} scale={0.5} speed={0.35} opacity={0.1} />
      <ambientLight intensity={0.4} />
      <pointLight position={[5, 5, 5]} intensity={0.8} color="#FACC15" />
    </>
  );
}

interface Hero3DBackgroundProps {
  className?: string;
}

function Hero3DBackground({ className }: Hero3DBackgroundProps) {
  const [show3D, setShow3D] = useState(false);
  
  useEffect(() => {
    // Only render 3D on desktop with no motion preference and no touch
    const prefersMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isDesktop = window.innerWidth >= 768 && !('ontouchstart' in window);
    setShow3D(!prefersMotion && isDesktop);
    
    // Re-check on resize and motion preference change
    const handleChange = () => {
      const prefersMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isDesktop = window.innerWidth >= 768 && !('ontouchstart' in window);
      setShow3D(!prefersMotion && isDesktop);
    };
    
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    window.addEventListener('resize', handleChange);
    mq.addEventListener('change', handleChange);
    return () => {
      window.removeEventListener('resize', handleChange);
      mq.removeEventListener('change', handleChange);
    };
  }, []);
  
  if (!show3D) {
    return (
      <div className={`absolute inset-0 overflow-hidden ${className}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-black" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-yellow-400/5 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-yellow-400/3 blur-2xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-yellow-400/2 blur-[100px]" />
      </div>
    );
  }
  
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-950/90 to-zinc-950 z-10 opacity-80" />
      <Canvas
        camera={{ position: [0, 0, 5], fov: 55 }}
        style={{ background: 'transparent' }}
        gl={{ antialias: true, alpha: true }}
      >
        <Scene3D />
      </Canvas>
    </div>
  );
}

const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  ctaText,
}) => {
  const [reducedMotion, setReducedMotion] = useState(false);
  
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 }
    }
  };
  
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
    }
  };

  return (
    <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden bg-zinc-950">
      {/* 3D Background */}
      <Hero3DBackground className="z-0" />

      {/* Layered gradients */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-zinc-950/40 via-transparent to-zinc-950/40" />
      
      <div className="container-custom relative z-20">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-5xl mx-auto text-center px-4"
        >
          <motion.div variants={itemVariants}>
            <div className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full border border-yellow-400/20 bg-yellow-400/5 mb-10">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-50"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400"></span>
              </span>
              <span className="text-xs text-yellow-400/80 font-semibold tracking-[0.25em] uppercase">India&apos;s #1 Crypto Platform</span>
            </div>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-bold text-white mb-10 leading-[1.02] tracking-tight">
              {title}
              <span className="text-yellow-400">.</span>
            </h1>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-14">
              {subtitle}
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            {typeof ctaText === 'string' ? (
              <button className="btn bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-10 py-4 text-lg rounded-xl">
                {ctaText}
              </button>
            ) : (
              ctaText
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator — no animation when user prefers reduced motion */}
      <motion.div 
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20"
        animate={{ y: reducedMotion ? 0 : [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] text-zinc-600 uppercase tracking-[0.3em]">Scroll</span>
          <div className="w-px h-10 overflow-hidden relative">
            <div className="absolute w-full h-full bg-gradient-to-b from-yellow-400/60 to-transparent" />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
